import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Search, Filter, Phone, Mail, Calendar, CheckCircle, Clock,
    RefreshCw, TrendingUp, Users, UserPlus, Target, ExternalLink, Trash2,
    AlertTriangle, Loader2
} from 'lucide-react';
import { leadsAPI, superAdminAPI } from '../../api/apiService';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';


const LeadsManagementPage = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [selectedLead, setSelectedLead] = useState(null);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [followUpDate, setFollowUpDate] = useState('');
    const [availableUsers, setAvailableUsers] = useState([]);
    const [searchUserQuery, setSearchUserQuery] = useState('');
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [targetStatus, setTargetStatus] = useState(null);
    const [deleteLeadId, setDeleteLeadId] = useState(null);
    const [deletingLead, setDeletingLead] = useState(false);
    const navigate = useNavigate();




    useEffect(() => {
        const cached = sessionStorage.getItem("leads_cache");
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setLeads(parsed);
                    setLoading(false);
                }
            } catch (e) {
                sessionStorage.removeItem("leads_cache");
            }
        }
        fetchLeads();
    }, []);

    const fetchLeads = async () => {
        if (!sessionStorage.getItem("leads_cache")) {
            setLoading(true);
        }
        try {
            const response = await leadsAPI.getAllRequests();
            setLeads(response.data);
            sessionStorage.setItem("leads_cache", JSON.stringify(response.data));
        } catch (error) {
            console.error("Error fetching leads:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await superAdminAPI.getAllUsers();
            // Filter only owners/independent users
            const owners = response.data.filter(u => !u.parent && !u.is_super_admin);
            setAvailableUsers(owners);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };



    const handleStatusUpdate = async (statusToSet) => {
        if (!selectedLead) return;

        // If clicking CONVERTED, and we haven't selected a user yet, show selection UI
        if (statusToSet === 'CONVERTED' && !selectedUserId) {
            setTargetStatus('CONVERTED');
            fetchUsers(); // Fetch owners to link
            return;
        }

        const finalStatus = statusToSet || targetStatus;
        if (!finalStatus) return;

        try {
            await leadsAPI.updateRequestStatus(selectedLead.id, {
                status: finalStatus,
                follow_up_date: followUpDate || null,
                converted_user: finalStatus === 'CONVERTED' ? selectedUserId : null
            });
            toast.success(`Lead marked as ${finalStatus}`);
            fetchLeads();
            handleCloseModal();
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("Failed to update status");
        }
    };

    const handleDeleteLead = (id) => {
        setDeleteLeadId(id);
    };

    const confirmDeleteLead = async () => {
        if (!deleteLeadId) return;
        setDeletingLead(true);
        try {
            await leadsAPI.deleteRequest(deleteLeadId);
            toast.success("Lead deleted successfully");
            sessionStorage.removeItem("leads_cache");
            setDeleteLeadId(null);
            fetchLeads();
        } catch (error) {
            console.error("Error deleting lead:", error);
            toast.error("Failed to delete lead");
        } finally {
            setDeletingLead(false);
        }
    };


    const handleCloseModal = () => {
        setIsStatusModalOpen(false);
        setSelectedLead(null);
        setFollowUpDate('');
        setSelectedUserId(null);
        setSearchUserQuery('');
        setTargetStatus(null);
    };

    const filteredUsers = availableUsers.filter(u =>
        u.business_name?.toLowerCase().includes(searchUserQuery.toLowerCase()) ||
        u.fullname?.toLowerCase().includes(searchUserQuery.toLowerCase()) ||
        u.phone?.includes(searchUserQuery)
    );




    const getStatusColor = (status) => {
        switch (status) {
            case 'NEW': return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30';
            case 'CONTACTED': return 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30';
            case 'SCHEDULED': return 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-500/30';
            case 'CONVERTED': return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30';
            case 'CLOSED': return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700/30';
            default: return 'bg-slate-500/10 text-slate-600 dark:text-slate-400';
        }
    };


    const filteredLeads = leads.filter(lead => {
        const matchesSearch =
            lead.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lead.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lead.phone.includes(searchTerm);

        const matchesStatus = statusFilter === 'ALL' || lead.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    return (
        <div className="p-6 bg-gray-50 dark:bg-slate-900 min-h-screen text-slate-900 dark:text-slate-100 transition-colors">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Leads Management</h1>
                    <p className="text-slate-500 dark:text-slate-400">Track and manage demo requests</p>
                </div>
                <button
                    onClick={fetchLeads}
                    className="p-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition"
                >
                    <RefreshCw size={20} className={`text-gray-600 dark:text-slate-400 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {/* Analytics Header */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden relative group">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 rounded-full -mr-8 -mt-8 px-4" />
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-500/10 rounded-2xl">
                            <Users className="text-blue-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Total Leads</p>
                            <h3 className="text-3xl font-black text-slate-900 dark:text-white leading-none mt-1">{leads.length}</h3>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden relative group">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 rounded-full -mr-8 -mt-8" />
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-amber-500/10 rounded-2xl">
                            <Target className="text-amber-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">New Leads</p>
                            <h3 className="text-3xl font-black text-slate-900 dark:text-white leading-none mt-1">{leads.filter(l => l.status === 'NEW').length}</h3>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden relative group">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full -mr-8 -mt-8" />
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-emerald-500/10 rounded-2xl">
                            <UserPlus className="text-emerald-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Converted</p>
                            <h3 className="text-3xl font-black text-slate-900 dark:text-white leading-none mt-1">{leads.filter(l => l.status === 'CONVERTED').length}</h3>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden relative group">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/5 rounded-full -mr-8 -mt-8" />
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-indigo-500/10 rounded-2xl">
                            <TrendingUp className="text-indigo-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Conv. Rate</p>
                            <h3 className="text-3xl font-black text-slate-900 dark:text-white leading-none mt-1">
                                {leads.length > 0 ? ((leads.filter(l => l.status === 'CONVERTED').length / leads.length) * 100).toFixed(1) : 0}%
                            </h3>
                        </div>
                    </div>
                </div>
            </div>


            {/* Filters */}
            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700/50 mb-6 flex flex-col sm:flex-row gap-4 justify-between items-center transition-all bg-opacity-50 backdrop-blur-sm">
                <div className="relative w-full sm:w-96">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search by business, person, or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-12 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900 dark:focus:ring-white outline-none transition-all text-sm"
                    />
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <Filter size={18} className="text-slate-400" />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="flex-1 sm:flex-none px-4 py-2.5 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-slate-900 dark:focus:ring-white outline-none transition-all text-sm appearance-none cursor-pointer pr-10"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m19 9-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center', backgroundSize: '1.25rem' }}
                    >
                        <option value="ALL">All Status</option>
                        <option value="NEW">New</option>
                        <option value="CONTACTED">Contacted</option>
                        <option value="SCHEDULED">Scheduled</option>
                        <option value="CONVERTED">Converted</option>
                        <option value="CLOSED">Closed</option>
                    </select>
                </div>
            </div>

            {/* Leads Table */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700/50 overflow-hidden transition-all">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                            <tr>
                                <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Business</th>
                                <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Contact Info</th>
                                <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Type</th>
                                <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Follow-up</th>
                                <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Status</th>
                                <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] text-right">Actions</th>
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="p-12 text-center text-slate-500 dark:text-slate-400 italic">Loading leads...</td>
                                </tr>
                            ) : filteredLeads.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="p-12 text-center text-slate-500 dark:text-slate-400 italic">No leads found matching your criteria.</td>
                                </tr>
                            ) : (
                                filteredLeads.map((lead) => (
                                    <tr key={lead.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-all border-l-4 border-transparent hover:border-slate-900 dark:hover:border-white">
                                        <td className="p-5">
                                            <div className="font-bold text-slate-900 dark:text-white mb-0.5">{lead.business_name}</div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">{lead.contact_person}</div>
                                        </td>
                                        <td className="p-5">
                                            <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 font-medium">
                                                <Phone size={12} className="text-slate-400" /> {lead.phone}
                                            </div>
                                            {lead.email && (
                                                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mt-1.5">
                                                    <Mail size={12} className="text-slate-400" /> {lead.email}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-5 text-sm">
                                            <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-lg text-[10px] font-black uppercase tracking-wider">
                                                {lead.business_type}
                                            </span>
                                        </td>
                                        <td className="p-5 text-sm">
                                            {lead.follow_up_date ? (
                                                <div className="flex flex-col">
                                                    <div className="text-amber-600 dark:text-amber-400 font-bold flex items-center gap-1.5">
                                                        <Clock size={12} /> {new Date(lead.follow_up_date).toLocaleDateString()}
                                                    </div>
                                                    <div className="text-[10px] text-slate-400 font-medium">Next follow-up</div>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col">
                                                    <div className="text-slate-900 dark:text-white font-bold">{new Date(lead.created_at).toLocaleDateString()}</div>
                                                    <div className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Created</div>
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-5">
                                            <div className="flex flex-col gap-2">
                                                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider text-center ${getStatusColor(lead.status)}`}>
                                                    {lead.status}
                                                </span>
                                                {lead.status === 'CONVERTED' && lead.converted_user && (
                                                    <button
                                                        onClick={() => {
                                                            const ownerId = typeof lead.converted_user === 'object' ? lead.converted_user.id : lead.converted_user;
                                                            navigate(`/super-admin?tab=users&ownerId=${ownerId}`);
                                                        }}
                                                        className="flex items-center gap-1 text-[9px] font-bold text-emerald-600 hover:text-emerald-700 transition-colors uppercase tracking-widest"
                                                    >
                                                        View Owner <ExternalLink size={10} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>

                                        <td className="p-5 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => {
                                                        setSelectedLead(lead);
                                                        setIsStatusModalOpen(true);
                                                    }}
                                                    className="px-4 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-900 dark:hover:bg-white text-slate-600 dark:text-slate-300 hover:text-white dark:hover:text-slate-900 text-[10px] font-black uppercase tracking-[0.1em] rounded-lg transition-all"
                                                >
                                                    Manage
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteLead(lead.id)}
                                                    className="p-1.5 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30 rounded-lg hover:bg-red-600 hover:text-white dark:hover:bg-red-600 dark:hover:text-white transition-all shadow-sm"
                                                    title="Delete Lead"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Status Modal */}
            {isStatusModalOpen && selectedLead && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-md w-full p-8 border border-slate-200 dark:border-slate-700"
                    >
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Update Status</h2>
                        <div className="mb-6 space-y-4">
                            <div className="p-5 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-700">
                                <p className="font-bold text-slate-900 dark:text-white text-lg">{selectedLead.business_name}</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mb-3">{selectedLead.contact_person}</p>
                                {selectedLead.message && (
                                    <div className="mt-2 text-xs text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700 italic">
                                        "{selectedLead.message}"
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Optional Follow-up Date</label>
                                <div className="relative">
                                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                    <input
                                        type="date"
                                        value={followUpDate}
                                        onChange={(e) => setFollowUpDate(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white transition-all"
                                    />
                                </div>
                            </div>
                        </div>


                        {targetStatus === 'CONVERTED' ? (
                            <div className="space-y-4 mb-8">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Link to Owner Account</label>
                                    <div className="relative">
                                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                        <input
                                            type="text"
                                            placeholder="Search owners by name or business..."
                                            value={searchUserQuery}
                                            onChange={(e) => setSearchUserQuery(e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white transition-all"
                                        />
                                    </div>
                                </div>

                                <div className="max-h-48 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                                    {filteredUsers.length === 0 ? (
                                        <p className="text-center py-4 text-xs text-slate-500 italic">No owners found. Create one first?</p>
                                    ) : (
                                        filteredUsers.map(user => (
                                            <button
                                                key={user.id}
                                                onClick={() => setSelectedUserId(user.id)}
                                                className={`w-full p-3 rounded-xl text-left transition-all border ${selectedUserId === user.id
                                                    ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-500 shadow-md'
                                                    : 'bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                                                    }`}
                                            >
                                                <div className="font-bold text-xs text-slate-900 dark:text-white">{user.fullname}</div>
                                                <div className="text-[10px] text-slate-500 dark:text-slate-400">{user.business_name}</div>
                                            </button>
                                        ))
                                    )}
                                </div>

                                <button
                                    onClick={() => handleStatusUpdate()}
                                    disabled={!selectedUserId}
                                    className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-[10px] font-black uppercase tracking-[0.2em] shadow-lg shadow-emerald-500/20 transition-all"
                                >
                                    Confirm Conversion
                                </button>
                                <button
                                    onClick={() => setTargetStatus(null)}
                                    className="w-full text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-colors"
                                >
                                    Back to Statuses
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-2.5 mb-8">
                                {['NEW', 'CONTACTED', 'SCHEDULED', 'CONVERTED', 'CLOSED'].map((status) => (
                                    <div key={status} className="space-y-2">
                                        <button
                                            onClick={() => handleStatusUpdate(status)}
                                            className={`w-full p-4 rounded-xl text-left text-[10px] font-black uppercase tracking-[0.15em] transition-all flex justify-between items-center ${selectedLead.status === status
                                                ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-xl scale-[1.02]'
                                                : 'bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500'
                                                }`}
                                        >
                                            {status}
                                            {selectedLead.status === status && <CheckCircle size={14} />}
                                        </button>

                                        {status === 'CONVERTED' && selectedLead.status !== 'CONVERTED' && (
                                            <div className="px-1 text-[9px] text-slate-400 font-bold uppercase tracking-widest italic flex items-center gap-1">
                                                <UserPlus size={10} /> Link to an Owner account
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}


                        <button
                            onClick={handleCloseModal}
                            className="w-full py-3 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                        >
                            Cancel
                        </button>
                    </motion.div>
                </div>
            )}

            {/* Custom In-Box Delete Confirmation Modal */}
            <AnimatePresence>
                {deleteLeadId && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => !deletingLead && setDeleteLeadId(null)}
                            className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm"
                        />

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-200 dark:border-slate-800 z-10 overflow-hidden"
                        >
                            {/* Red glow accent */}
                            <div className="absolute -top-12 -right-12 w-32 h-32 bg-red-500/10 rounded-full blur-2xl pointer-events-none" />

                            <div className="flex items-start gap-4 mb-4">
                                <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-2xl flex-shrink-0">
                                    <AlertTriangle className="w-6 h-6" />
                                </div>
                                <div className="space-y-1">
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                        Delete Lead Record?
                                    </h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                                        Are you sure you want to delete this lead? This action cannot be undone and will permanently remove the record from the system.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-slate-100 dark:border-slate-800">
                                <button
                                    type="button"
                                    disabled={deletingLead}
                                    onClick={() => setDeleteLeadId(null)}
                                    className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={deletingLead}
                                    onClick={confirmDeleteLead}
                                    className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-red-600 hover:bg-red-700 active:scale-95 shadow-lg shadow-red-600/20 transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {deletingLead ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Deleting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 className="w-4 h-4" />
                                            <span>Yes, Delete</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LeadsManagementPage;
